import {
  AddIcon,
  Button,
  Flex,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import {
  Form,
  useFieldArray,
  useFormContext,
} from '@iheartjane/reefer-hook-form';
import { useEffect, useRef } from 'react';

interface ExternalLinksFieldProps {
  initialLinks?: Array<{ label: string; url: string }>;
}

export const ExternalLinksField = ({
  initialLinks,
}: ExternalLinksFieldProps) => {
  const { control, getValues, trigger } = useFormContext();
  const { fields, append, remove, replace } = useFieldArray<
    Record<string, Array<{ label: string; url: string }>>
  >({
    control,
    name: 'external_links',
  });
  const initialized = useRef(false);

  useEffect(() => {
    if (initialLinks && !initialized.current) {
      initialized.current = true;
      replace(initialLinks);
    }
  }, [initialLinks, replace]);

  return (
    <Flex flexDirection="column" gap={8}>
      <Typography variant="body" color="grays-mid">
        Link to agreement(s) (Optional)
      </Typography>
      {fields.map((field, index) => (
        <Flex key={field.id} gap={12} alignItems="flex-start">
          <Flex grow={1} basis="0" minWidth={0}>
            <Form.TextField
              name={`external_links.${index}.label`}
              label="Link label"
              labelHidden
              placeholder="Add label"
              defaultValue={field.label}
              maxLength={35}
              width="100%"
              onChange={() => trigger(`external_links.${index}.url`)}
              validate={(value: string) => {
                const url = getValues(`external_links.${index}.url`) as string;
                if (url?.trim() && !value?.trim()) {
                  return 'Label is required';
                }
                return true;
              }}
            />
          </Flex>
          <Flex grow={2} basis="0" minWidth={0}>
            <Form.TextField
              name={`external_links.${index}.url`}
              label="Link URL"
              labelHidden
              placeholder="https://"
              defaultValue={field.url}
              width="100%"
              onChange={() => trigger(`external_links.${index}.label`)}
              validate={(value: string) => {
                if (value?.trim() && !/^https?:\/\/.+/i.test(value.trim())) {
                  return 'URL must start with http:// or https://';
                }
                return true;
              }}
            />
          </Flex>
          {/* Match TextField rendered height so the icon stays centered
              with the input when validation errors expand the row. */}
          <Flex
            alignItems="center"
            justifyContent="center"
            shrink={0}
            style={{ height: 52 }}
          >
            {index === 0 && fields.length < 3 ? (
              <Button.Icon
                icon={<AddIcon size="sm" color="grays-mid" />}
                label="Add link"
                onClick={() => append({ label: '', url: '' })}
              />
            ) : index > 0 ? (
              <Button.Icon
                icon={<TrashIcon color="grays-mid" />}
                label="Remove link"
                onClick={() => remove(index)}
              />
            ) : (
              <div aria-hidden="true" style={{ visibility: 'hidden' }}>
                <Button.Icon icon={<AddIcon />} label="" />
              </div>
            )}
          </Flex>
        </Flex>
      ))}
      <Typography variant="body" color="grays-mid">
        Maximum 3 links. URLs must start with http:// or https://
      </Typography>
    </Flex>
  );
};
